/**
 * <FeaturedDealerHero />
 *
 * Top-of-marketplace hero that surfaces this week's rotating dealer.
 * Renders nothing when the API has no eligible dealer, so the caller can
 * always mount the component safely.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { Sparkle } from "lucide-react";
import { api } from "../../lib/api";
import type { FeaturedDealerResponse } from "../../lib/types";
import { Badge } from "../ui/Badge";
import { ProductCard } from "./ProductCard";

export function FeaturedDealerHero() {
  const [data, setData] = React.useState<FeaturedDealerResponse | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<FeaturedDealerResponse>("/dealers/featured-week");
        if (!cancelled) setData(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!data || !data.dealer) return null;

  const { dealer, listings, week_number } = data;
  const topThree = listings.slice(0, 3);

  return (
    <section
      data-testid="featured-dealer-hero"
      className="relative overflow-hidden rounded-3xl bg-ink text-white p-6 md:p-10 mb-10"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 60% at 85% 15%, rgba(225,29,46,0.95) 0%, transparent 65%)",
        }}
      />
      <div className="relative grid lg:grid-cols-[1.1fr_2fr] gap-8 items-start">
        <div>
          <Badge variant="brand" size="sm" className="mb-4">
            <Sparkle className="h-3.5 w-3.5 mr-1" aria-hidden />
            Dealer of the week · #{week_number}
          </Badge>
          <h2 className="font-display text-4xl md:text-5xl font-black leading-[1.05] mb-3">
            Meet <span className="text-brand">@{dealer.username}</span>
          </h2>
          {dealer.full_name && (
            <p className="text-white/80 text-lg mb-3">{dealer.full_name}</p>
          )}
          {dealer.bio && (
            <p className="text-white/70 max-w-md mb-5 leading-relaxed">{dealer.bio}</p>
          )}
          <Link
            to={`/dealer/${dealer.id}`}
            data-testid="featured-dealer-visit"
            className="inline-flex items-center font-bold text-white border-b-2 border-brand pb-0.5 hover:text-brand transition-colors"
          >
            Visit storefront →
          </Link>
        </div>

        {topThree.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {topThree.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl text-ink">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
